/**
 * AI-assisted reply suggestions + English proofreading.
 *
 * Live inbox: POST `/api/v1/agent/inbox/writing-assistant` (OpenAI via human-agent-service).
 * Optional `VITE_AI_WRITING_URL` overrides for a custom endpoint; otherwise local stubs when inbox is off.
 */

import { agentApiFetch } from "@/lib/agentApiFetch";
import { isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";
import { generateLocalSuggestReplies } from "@/lib/aiSuggestReplies";
import { lightProofreadEnglish } from "@/lib/writingAssistantStub";

const stubDelay = (ms = 220) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {Array<{ sender?: string, text?: string, type?: string }>} messages
 */
export function buildConversationContextForAi(messages, maxLines = 14) {
  const lines = (messages ?? [])
    .filter((m) => m && (m.type === "system" ? false : m.sender === "user" || m.sender === "agent" || m.sender === "bot"))
    .slice(-maxLines)
    .map((m) => `${String(m.sender ?? "unknown")}: ${String(m.text ?? "").trim()}`)
    .filter((l) => l.length > 2);
  return lines.join("\n");
}

/**
 * @param {object} body
 * @param {string | null | undefined} workspaceId
 */
async function postWritingAssistant(body, workspaceId) {
  return agentApiFetch("/api/v1/agent/inbox/writing-assistant", {
    method: "POST",
    workspaceId: workspaceId ?? null,
    body,
  });
}

/**
 * @param {{ messages: unknown[], draftText?: string, ticketSubject?: string, ticketSummary?: string, workspaceId?: string | null }} input
 * @returns {Promise<{ data: { suggestions: string[] } }>}
 */
export async function suggestAgentReplies(input) {
  const messages = Array.isArray(input.messages) ? input.messages : [];
  const draftText = typeof input.draftText === "string" ? input.draftText : "";
  const ctx = buildConversationContextForAi(messages);

  if (isHumanAgentInboxLive()) {
    const json = await postWritingAssistant(
      {
        mode: "suggest",
        context: ctx,
        draft: draftText,
        ticket: {
          subject: input.ticketSubject ?? "",
          summary: input.ticketSummary ?? "",
        },
      },
      input.workspaceId,
    );
    const suggestions = Array.isArray(json?.suggestions) ? json.suggestions.map(String).filter(Boolean) : [];
    return {
      data: {
        suggestions: suggestions.length
          ? suggestions
          : generateLocalSuggestReplies({
              messages,
              draftText,
              ticketSubject: input.ticketSubject,
              ticketSummary: input.ticketSummary,
            }),
      },
    };
  }

  const url = typeof import.meta !== "undefined" && import.meta.env?.VITE_AI_WRITING_URL;
  if (url && String(url).trim()) {
    const key = typeof import.meta !== "undefined" ? import.meta.env?.VITE_AI_WRITING_API_KEY : "";
    const res = await fetch(String(url).trim(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(key ? { Authorization: `Bearer ${String(key)}` } : {}),
      },
      body: JSON.stringify({
        mode: "suggest",
        context: ctx,
        draft: draftText,
        ticket: {
          subject: input.ticketSubject ?? "",
          summary: input.ticketSummary ?? "",
        },
      }),
    });
    if (!res.ok) {
      throw new Error(`AI suggestions failed (${res.status}). Check VITE_AI_WRITING_URL.`);
    }
    const json = await res.json();
    const suggestions = Array.isArray(json?.suggestions) ? json.suggestions.map(String).filter(Boolean) : [];
    return {
      data: {
        suggestions: suggestions.length
          ? suggestions
          : generateLocalSuggestReplies({
              messages,
              draftText,
              ticketSubject: input.ticketSubject,
              ticketSummary: input.ticketSummary,
            }),
      },
    };
  }

  await stubDelay(180);
  const suggestions = generateLocalSuggestReplies({
    messages,
    draftText,
    ticketSubject: input.ticketSubject,
    ticketSummary: input.ticketSummary,
  });
  return { data: { suggestions } };
}

/**
 * @param {{ text: string, workspaceId?: string | null }} input
 * @returns {Promise<{ data: { text: string } }>}
 */
export async function proofreadEnglish(input) {
  const raw = typeof input.text === "string" ? input.text : "";

  if (isHumanAgentInboxLive()) {
    const json = await postWritingAssistant(
      {
        mode: "proofread",
        context: "",
        draft: raw,
        ticket: {},
      },
      input.workspaceId,
    );
    const text = typeof json?.text === "string" && json.text.trim() ? json.text : lightProofreadEnglish(raw);
    return { data: { text } };
  }

  const url = typeof import.meta !== "undefined" && import.meta.env?.VITE_AI_WRITING_URL;
  if (url && String(url).trim()) {
    const key = typeof import.meta !== "undefined" ? import.meta.env?.VITE_AI_WRITING_API_KEY : "";
    const res = await fetch(String(url).trim(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(key ? { Authorization: `Bearer ${String(key)}` } : {}),
      },
      body: JSON.stringify({
        mode: "proofread",
        context: "",
        draft: raw,
        ticket: {},
      }),
    });
    if (!res.ok) {
      throw new Error(`Proofread failed (${res.status}). Check VITE_AI_WRITING_URL.`);
    }
    const json = await res.json();
    const text = typeof json?.text === "string" && json.text.trim() ? json.text : lightProofreadEnglish(raw);
    return { data: { text } };
  }

  await stubDelay(120);
  return { data: { text: lightProofreadEnglish(raw) } };
}
